import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUtilityServices, deleteUtilityService } from '../../api/utilityServices';
import { fetchSuppliers } from '../../api/suppliers';
import { UtilityService, UtilityType } from '../../types/utility.types';
import AddUtilityServiceDialog from './AddUtilityServiceDialog';
import EditUtilityServiceDialog from './EditUtilityServiceDialog';
import ViewUtilityServiceDialog from './ViewUtilityServiceDialog';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface UtilityServicesProps {
  propertyId: string;
  clientId: string;
  services: UtilityService[];
  onServicesChange: (services: UtilityService[]) => void;
}

const UtilityServices: React.FC<UtilityServicesProps> = ({ propertyId, clientId, services, onServicesChange }) => {
  // Remove the services state since it's now passed as a prop
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<UtilityService | null>(null);
  const [viewServiceDialogOpen, setViewServiceDialogOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const [fetchedServices, fetchedSuppliers] = await Promise.all([
        getUtilityServices(clientId, propertyId),
        fetchSuppliers()
      ]);
      setSuppliers(fetchedSuppliers || []);
      onServicesChange(fetchedServices || []);
    } catch (err: any) {
      
      // Handle 404 specifically as "no utilities" case
      if (err.response?.status === 404) {
        onServicesChange([]);
        setError(null);
      } else {
        setError('Failed to load utility services');
        toast.error('Failed to load utility services');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [propertyId, clientId]);

  useEffect(() => {
    setLoading(false);
  }, [services]);

  const handleAddClick = () => {
    // Navigate to the new contract form instead of opening the dialog
    navigate(`/utilities/contracts/new?clientId=${clientId}&propertyId=${propertyId}`);
  };

  const handleServiceAdded = async () => {
    await fetchServices();
    setAddDialogOpen(false);
  };

  const handleEditClick = (service: UtilityService) => {
    setSelectedService(service);
    setEditDialogOpen(true);
  };

  const handleServiceEdited = async () => {
    await fetchServices();
    setEditDialogOpen(false);
    setSelectedService(null);
  };

  const handleDeleteClick = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this utility service?')) {
      return;
    }

    try {
      setError(null);
      await deleteUtilityService(clientId, propertyId, serviceId);
      // Update local state immediately
      onServicesChange(services.filter(s => s._id !== serviceId));
    } catch (err) {
      
      setError('Failed to delete utility service. Please try again.');
      // Refresh the services list in case of error
      await fetchServices();
    }
  };

  const getStatusColor = (status: UtilityService['status']) => {
    switch (status) {
      case 'LIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Format service type in proper case for display
  const formatServiceType = (type: UtilityType | string): string => {
    if (!type) return '';
    
    // Handle special cases
    if (type === 'PPA Export Contract') return 'PPA Export Contract';
    if (type === 'EV') return 'EV';
    
    // Convert other types to proper case
    return type.toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Contract
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {services.length === 0 && !error && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary" gutterBottom>
            No contracts found for this property
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click the "Add Contract" button above to add your first contract.
          </Typography>
        </Box>
      )}

      {services.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><Typography fontWeight="bold">Contract Type</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Supplier</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Account Number</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Status</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Contract Start</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Contract End</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service) => (
                <TableRow 
                  key={service._id}
                  hover
                  onClick={() => {
                    setSelectedService(service);
                    setViewServiceDialogOpen(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{formatServiceType(service.serviceType)}</TableCell>
                  <TableCell>
    {suppliers.find(s => s._id === service.supplier)?.name || service.supplier || 'N/A'}
  </TableCell>
                  <TableCell>{service.accountNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={service.status}
                      color={getStatusColor(service.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {service.contractStartDate
                      ? new Date(service.contractStartDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {service.contractEndDate
                      ? new Date(service.contractEndDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddUtilityServiceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleServiceAdded}
        propertyId={propertyId}
        clientId={clientId}
      />

      {selectedService && (
        <ViewUtilityServiceDialog
          open={viewServiceDialogOpen}
          onClose={() => setViewServiceDialogOpen(false)}
          onEdit={() => {
            setViewServiceDialogOpen(false);
            handleEditClick(selectedService);
          }}
          onDelete={() => {
            setViewServiceDialogOpen(false);
            handleDeleteClick(selectedService._id);
          }}
          service={selectedService}
          propertyId={propertyId}
          clientId={clientId}
        />
      )}

      {selectedService && (
        <EditUtilityServiceDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedService(null);
          }}
          onEdit={handleServiceEdited}
          service={selectedService}
          propertyId={propertyId}
          clientId={clientId}
        />
      )}
    </Box>
  );
};

export default UtilityServices;
